/**
 * @fileoverview Sensitive data sanitization utilities
 * @module utils/sanitize
 */

import {
  REDACTED_VALUE,
  redactEmail,
  redactPhone,
  redactSensitiveText,
} from '@puffin-core/redaction';
import { isEmailAddress } from '@puffin-core/email-address';
import { SENSITIVE_FIELDS } from '../constants.js';

// The ONE replacement text — `@puffin-core/redaction`'s, shared with the server logger (Q-SG-24).
export { REDACTED_VALUE };

/**
 * Value-scan a string with this logger's vocabulary: the sensitive field names plus the caller's.
 * @param {*} value - Any value; only strings are scanned
 * @param {string[]} additionalFields - The caller's extra field names
 * @returns {*} The redacted string, or the value unchanged
 */
function redactText(value, additionalFields) {
  return redactSensitiveText(value, { fields: [...SENSITIVE_FIELDS, ...additionalFields] });
}

/**
 * Check if a key matches a sensitive field pattern.
 * Internal helper — `sanitizeData` always passes a resolved additionalFields array.
 * @param {string} key - The key to check
 * @param {string[]} additionalFields - Additional fields to check
 * @returns {boolean} True if the key is sensitive
 */
function isSensitiveKey(key, additionalFields) {
  const lowerKey = key.toLowerCase();
  const allFields = [...SENSITIVE_FIELDS, ...additionalFields];
  return allFields.some((field) => lowerKey.includes(field.toLowerCase()));
}

/** A whole value that is one phone number: digits and the separators a number is written with. */
const PHONE_VALUE_SHAPE = /^[\d\s().+-]+$/;

/**
 * The partial mask a keyed email / phone field gets — only when the WHOLE value is that shape
 * (MUTATION-BURNDOWN-001 Q-MB-25). Anything else under such a key is sanitized like any other value:
 * an object or list recurses, a string is value-scanned. The key used to short-circuit both, so
 * `{ emailSettings: { apiKey } }` shipped the caller's object raw, `{ emails: [address] }` unmasked,
 * and `{ phoneNote: 'password=…' }` kept the password.
 *
 * @param {string} key
 * @param {*} value
 * @returns {?Function} The mask to apply, or null when the value is sanitized normally
 */
function keyedMaskFor(key, value) {
  if (typeof value !== 'string') return null;
  const lowerKey = key.toLowerCase();
  if (lowerKey.includes('email') && isEmailAddress(value)) return redactEmail;
  if (lowerKey.includes('phone') && PHONE_VALUE_SHAPE.test(value)) return redactPhone;
  return null;
}

/**
 * Sanitize data by redacting sensitive fields — by KEY (a sensitive field is blanked whole; an email
 * or phone field whose whole value is an address / number is partially masked) and by VALUE: every
 * string kept, at any depth, is scanned by
 * `@puffin-core/redaction`'s `redactSensitiveText` for secrets written into free text — the ONE
 * implementation the server logger uses too (Q-SG-23 / Q-SG-24).
 * @param {*} data - Data to sanitize
 * @param {Object} [options] - Sanitization options
 * @param {string[]} [options.additionalFields] - Additional fields to redact
 * @param {number} [options.maxDepth=10] - Maximum recursion depth
 * @param {number} [currentDepth=0] - Current recursion depth (internal)
 * @returns {*} Sanitized data
 */
export function sanitizeData(data, options = {}, currentDepth = 0) {
  const { additionalFields = [], maxDepth = 10 } = options;

  // Handle null/undefined
  if (data === null || data === undefined) {
    return data;
  }

  // Prevent infinite recursion
  if (currentDepth >= maxDepth) {
    return '[MAX_DEPTH_EXCEEDED]';
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) => sanitizeData(item, options, currentDepth + 1));
  }

  // Handle objects
  if (typeof data === 'object') {
    const result = {};
    for (const [key, value] of Object.entries(data)) {
      const keyedMask = keyedMaskFor(key, value);
      // Check if this is a sensitive field first (takes priority)
      if (isSensitiveKey(key, additionalFields)) {
        result[key] = REDACTED_VALUE;
      } else if (keyedMask !== null) {
        result[key] = keyedMask(value);
      } else if (typeof value === 'object' && value !== null) {
        // Recursively sanitize nested objects
        result[key] = sanitizeData(value, options, currentDepth + 1);
      } else {
        result[key] = redactText(value, additionalFields);
      }
    }
    return result;
  }

  // A string is value-scanned; every other primitive is returned as-is.
  return redactText(data, additionalFields);
}
